import { Controller } from "@hotwired/stimulus"

// A continuously drifting row that the visitor can still stop and steer.
//
// The track holds the item set rendered TWICE and travels exactly one set width,
// so the second copy is already in place when the first leaves — the loop closes
// with nothing to rewind. That is Kip's technique (kip.com.au), which they do in
// pure CSS.
//
// CSS alone cannot be stepped, reversed or stopped on demand, and Maria asked for
// all three. So the same keyframes run through the Web Animations API instead,
// which hands back an Animation with pause(), a settable currentTime, and a
// playbackRate that accepts negative values. No requestAnimationFrame loop: the
// browser still drives the motion off the compositor.
export default class extends Controller {
  static targets = ["track", "viewport", "toggle", "toggleIcon", "toggleLabel"]
  static values = {
    secondsPerCard: { type: Number, default: 7 },
    scrubRate: { type: Number, default: 6 }
  }

  connect() {
    this.paused = false
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.build()

    this.handleResize = this.debounce(() => this.rebuild(), 150)
    window.addEventListener("resize", this.handleResize)

    // Hover is the discoverable pause on a desktop; focus covers keyboard users
    // tabbing through the review links. Neither overrides an explicit stop.
    this.element.addEventListener("mouseenter", this.suspend)
    this.element.addEventListener("mouseleave", this.resume)
    this.element.addEventListener("focusin", this.suspend)
    this.element.addEventListener("focusout", this.resume)
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.element.removeEventListener("mouseenter", this.suspend)
    this.element.removeEventListener("mouseleave", this.resume)
    this.element.removeEventListener("focusin", this.suspend)
    this.element.removeEventListener("focusout", this.resume)
    clearTimeout(this.scrubTimer)
    this.animation?.cancel()
  }

  // --- the animation ------------------------------------------------------

  build() {
    if (!this.hasTrackTarget) return

    // The set is rendered twice, so one set is half the track. Measured rather
    // than computed from a card width, which would have to know the gutters.
    this.setWidth = this.trackTarget.scrollWidth / 2
    if (this.setWidth <= 0) return

    this.animation = this.trackTarget.animate(
      [
        { transform: "translateX(0)" },
        { transform: `translateX(-${this.setWidth}px)` }
      ],
      { duration: this.durationMs, iterations: Infinity, easing: "linear" }
    )

    // Someone who asked their OS for less motion gets it stopped, not started —
    // but the arrows still work, so the reviews stay browsable.
    if (this.reducedMotion.matches) {
      this.paused = true
      this.animation.pause()
    }
    this.syncToggle()
  }

  rebuild() {
    if (!this.animation) return this.build()

    const progress = this.animation.currentTime / this.durationMs
    this.animation.cancel()
    this.build()
    if (this.animation) this.animation.currentTime = (progress % 1) * this.durationMs
    if (this.paused) this.animation.pause()
  }

  get itemCount() {
    return this.trackTarget.children.length / 2
  }

  get durationMs() {
    return this.itemCount * this.secondsPerCardValue * 1000
  }

  // --- controls -----------------------------------------------------------

  toggle() {
    this.paused = !this.paused
    this.paused ? this.animation?.pause() : this.animation?.play()
    this.syncToggle()
  }

  next() {
    this.scrub(1)
  }

  previous() {
    this.scrub(-1)
  }

  // Reaching for an arrow means wanting control, so a scrub also stops the drift
  // rather than letting it wander off again under the reader.
  scrub(direction) {
    if (!this.animation) return

    this.paused = true
    this.syncToggle()

    const stepMs = this.secondsPerCardValue * 1000

    // An infinite animation clamps at currentTime 0, so running backwards from
    // the very start would simply stall. One whole iteration forward is the same
    // frame visually — the loop is seamless — so step there first, then reverse.
    if (direction < 0 && this.animation.currentTime < stepMs) {
      this.animation.currentTime += this.durationMs
    }

    this.animation.playbackRate = direction * this.scrubRateValue
    this.animation.play()

    clearTimeout(this.scrubTimer)
    this.scrubTimer = setTimeout(() => {
      this.animation.pause()
      this.animation.playbackRate = 1
    }, stepMs / this.scrubRateValue)
  }

  // Hover/focus pause. Arrow functions so they can be removed by reference.
  suspend = () => {
    if (!this.paused) this.animation?.pause()
  }

  resume = () => {
    if (!this.paused) this.animation?.play()
  }

  syncToggle() {
    if (!this.hasToggleTarget) return

    this.toggleTarget.setAttribute("aria-pressed", String(this.paused))
    this.toggleTarget.setAttribute("aria-label", this.paused ? "Play the reviews" : "Pause the reviews")
    if (this.hasToggleLabelTarget) this.toggleLabelTarget.textContent = this.paused ? "Play" : "Pause"
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.toggle("fa-play", this.paused)
      this.toggleIconTarget.classList.toggle("fa-pause", !this.paused)
    }
  }

  debounce(fn, wait) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn(...args), wait)
    }
  }
}
